<template>
  <div id="comments">
    <div class="mn-b-medium">
      
      <h3 class="mn-b-regular">{{ t('community.comments.title') }} ({{ comments.length }})</h3>

      <div class="mn-b-small">
        <!-- Форму ввода показываем только авторизованным; иначе — CTA на логин
             (раньше форма висела для всех, а логин звался только если что-то введено). -->
        <form v-if="owner" @submit.prevent="handleCommentSubmit" class="bg-white radius-medium pd-medium">
          <textarea
            v-model="commentContent"
            :placeholder="t('community.comments.placeholder')"
            class="p-regular w-100"
            style="resize: none;"
          ></textarea>
          <button type="submit" class="mn-l-auto button--main pd-r-regular pd-l-regular pd-thin button">
            {{ t('community.comments.send') }}
          </button>
        </form>

        <div v-else class="bg-white radius-medium pd-medium gap-small flex-v-center flex-nowrap flex">
          <p class="p-regular">{{ t('community.comments.loginToComment') }}</p>
          <button @click="promptAuth" class="mn-l-auto button--main pd-r-regular pd-l-regular pd-thin button">
            {{ t('community.comments.login') }}
          </button>
        </div>
      </div>
    </div>

    <Comment
      v-for="comment in comments"
      :key="comment._id"
      :comment="comment"
      :target="target"
      :type="type"
      :owner="owner"
      @reply="handleReply"
      @load-more="loadMoreChildren"
      class="comment mn-b-medium"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, getCurrentInstance } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Preferences } from '@capacitor/preferences';

import Comment from './Comment.vue';
import $axios from '@martyrs/src/modules/core/views/utils/axios-instance.js';

const { t } = useI18n();

const props = defineProps([
  'target', 
  'owner', 
  'type'
]);

const route = useRoute();
const { proxy } = getCurrentInstance();

const comments = ref([]);
const commentContent = ref('');

const DRAFT_KEY = `comment_draft_${props.type}_${props.target}`;

// Загружаем сохраненный черновик при монтировании
onMounted(async () => {
  // Проверяем наличие черновика
  const { value } = await Preferences.get({ key: DRAFT_KEY });
  
  if (value) {
    commentContent.value = value;
    // Очищаем черновик после загрузки
    await Preferences.remove({ key: DRAFT_KEY });
  }
});

const fetchComments = async () => {
  try {
    const response = await $axios.get('/comments/read', {
      params: {
        target: props.target,
        type: props.type,
        user: props.owner,
        format: 'tree',
        maxDepth: 2
      }
    });
    comments.value = response.data;
  } catch (error) {
    console.error('Error fetching comments:', error);
  }
};

const handleCommentSubmit = async () => {
  if (!commentContent.value) {
    return;
  }

  // Проверяем авторизацию
  if (!props.owner) {
    // Сохраняем черновик
    await Preferences.set({
      key: DRAFT_KEY,
      value: commentContent.value
    });
    
    // Показываем auth popup
    await promptAuth();

    return;
  }

  // Отправляем комментарий
  await submitComment();
};

// Вызов окна авторизации (локализованный) — используется и кнопкой-CTA под формой.
const promptAuth = async () => {
  await proxy.$showAuth({
    title: t('community.comments.auth.title'),
    subtitle: t('community.comments.auth.subtitle'),
    returnUrl: route.fullPath
  });
};

const submitComment = async () => {
  try {
    const response = await $axios.post('/comments/create', {
      target: props.target,
      user: props.owner,
      type: props.type,
      content: commentContent.value,
      creator: {
        target: props.owner,
        type: 'User',
        hidden: false
      },
      owner: {
        target: props.owner,
        type: 'User'
      }
    });

    response.data.owner = props.owner;
    comments.value.push(response.data);

    commentContent.value = '';
  } catch (error) {
    console.error(error);
  }
};

const handleReply = async (parentId, content) => {
  // Проверяем авторизацию для ответов
  if (!props.owner) {
    // Сохраняем черновик ответа
    await Preferences.set({
      key: `${DRAFT_KEY}_reply_${parentId}`,
      value: content
    });
    
    await promptAuth();

    return;
  }

  try {
    const response = await $axios.post('/comments/create', {
      content,
      target: props.target,
      type: props.type,
      user: props.owner,
      parent: parentId,
      format: 'tree',
      creator: {
        target: props.owner,
        type: 'User',
        hidden: false
      },
      owner: {
        target: props.owner,
        type: 'User'
      }
    });

    const newComment = response.data;
    updateCommentTree(comments.value, parentId, newComment);
  } catch (error) {
    console.error('Error posting reply:', error);
  }
};

const loadMoreChildren = async (commentId, depth) => {
  try {
    const response = await $axios.get('/comments/read', {
      params: {
        target: props.target,
        type: props.type,
        user: props.owner,
        parentId: commentId,
        depth: depth,
        maxDepth: 10
      }
    });

    updateCommentTree(comments.value, commentId, response.data, response.data.hasMore, true);
  } catch (error) {
    console.error('Error loading more comments:', error);
  }
};

const updateCommentTree = (comments, parentId, newData, hasMore, isLoadMore = false) => {
  for (let i = 0; i < comments.length; i++) {
    if (comments[i]._id === parentId) {
      if (isLoadMore) {
        comments[i].children = [...comments[i].children, ...newData];
        comments[i].hasMore = hasMore;
      } else {
        comments[i].children.push(newData);
      }
      return true;
    }
    if (comments[i].children && updateCommentTree(comments[i].children, parentId, newData, hasMore, isLoadMore)) {
      return true;
    }
  }
  return false;
};

fetchComments();
</script>